import React, { useState } from 'react';
import { 
  BarChart3, 
  TrendingUp, 
  DollarSign, 
  Eye,
  Target,
  Calendar,
  Filter,
  Download,
  Settings,
  Play,
  Pause,
  Edit,
  Plus,
  AlertCircle,
  CheckCircle,
  Clock,
  Users
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const VendorAnalyticsAdvertising = () => {
  const [activeTab, setActiveTab] = useState('analytics');
  const [dateRange, setDateRange] = useState('30d');

  // Mock analytics data for home improvement business
  const analyticsData = {
    quoteSubmissions: [
      { month: 'Jul', quotes: 12, conversions: 8 },
      { month: 'Aug', quotes: 18, conversions: 12 },
      { month: 'Sep', quotes: 15, conversions: 10 },
      { month: 'Oct', quotes: 22, conversions: 16 },
      { month: 'Nov', quotes: 28, conversions: 19 }
    ],
    projectCategories: [
      { name: 'Kitchen Remodeling', value: 35, color: '#10b981' },
      { name: 'Bathroom Renovation', value: 25, color: '#3b82f6' },
      { name: 'Outdoor Construction', value: 20, color: '#f59e0b' },
      { name: 'Flooring', value: 15, color: '#ef4444' },
      { name: 'Other', value: 5, color: '#8b5cf6' }
    ],
    performanceMetrics: {
      responseTime: { current: 2.3, previous: 3.1, unit: 'hours' },
      winRate: { current: 68, previous: 62, unit: '%' },
      avgProjectValue: { current: 8500, previous: 7800, unit: '$' },
      clientSatisfaction: { current: 4.7, previous: 4.5, unit: '/5' }
    }
  };

  // Mock advertising campaigns data
  const campaigns = [
    {
      id: 1,
      name: 'Kitchen Remodeling Premium',
      status: 'active',
      budget: 500,
      spent: 342,
      impressions: 12500,
      clicks: 89,
      leads: 12,
      ctr: 0.71,
      cpl: 28.50,
      keywords: ['kitchen renovation', 'kitchen remodel', 'custom cabinets'],
      startDate: '2025-11-01',
      endDate: '2025-11-30'
    },
    {
      id: 2,
      name: 'Bathroom Renovation Boost',
      status: 'active',
      budget: 300,
      spent: 198,
      impressions: 8200,
      clicks: 45,
      leads: 6,
      ctr: 0.55,
      cpl: 33.00,
      keywords: ['bathroom remodel', 'bathroom renovation', 'tile installation'],
      startDate: '2025-11-01',
      endDate: '2025-11-30'
    },
    {
      id: 3,
      name: 'Deck Construction Special',
      status: 'paused',
      budget: 400,
      spent: 156,
      impressions: 6800,
      clicks: 32,
      leads: 4,
      ctr: 0.47,
      cpl: 39.00,
      keywords: ['deck construction', 'outdoor deck', 'composite decking'],
      startDate: '2025-10-15',
      endDate: '2025-11-15'
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'paused': return 'bg-yellow-100 text-yellow-800';
      case 'ended': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return <CheckCircle className="w-4 h-4" />;
      case 'paused': return <Pause className="w-4 h-4" />;
      case 'ended': return <Clock className="w-4 h-4" />;
      default: return <AlertCircle className="w-4 h-4" />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-32 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Analytics & Advertising</h1>
          <p className="text-gray-600">Track your performance and optimize your advertising campaigns</p>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-1 mb-8">
          <div className="flex">
            <button
              onClick={() => setActiveTab('analytics')}
              className={`flex-1 px-6 py-3 text-sm font-medium rounded-lg transition-colors ${
                activeTab === 'analytics' ?'bg-green-600 text-white' :'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              Performance Analytics
            </button>
            <button
              onClick={() => setActiveTab('advertising')}
              className={`flex-1 px-6 py-3 text-sm font-medium rounded-lg transition-colors ${
                activeTab === 'advertising' ?'bg-green-600 text-white' :'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              Advertising Campaigns
            </button>
          </div>
        </div>

        {activeTab === 'analytics' && (
          <div className="space-y-8">
            {/* Date Range Selector */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div className="flex items-center gap-4">
                  <Calendar className="w-5 h-5 text-gray-500" />
                  <span className="text-sm font-medium text-gray-700">Date Range:</span>
                  <select
                    value={dateRange}
                    onChange={(e) => setDateRange(e?.target?.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    <option value="7d">Last 7 days</option>
                    <option value="30d">Last 30 days</option>
                    <option value="90d">Last 90 days</option>
                    <option value="6m">Last 6 months</option>
                    <option value="1y">Last year</option>
                  </select>
                </div>
                <div className="flex items-center gap-3">
                  <button className="flex items-center gap-2 px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                    <Filter className="w-4 h-4" />
                    Filter
                  </button>
                  <button className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors">
                    <Download className="w-4 h-4" />
                    Export Report
                  </button>
                </div>
              </div>
            </div>

            {/* Performance Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Clock className="w-5 h-5 text-blue-600" />
                  </div>
                  <span className="text-xs text-green-600 font-medium">
                    -{((analyticsData?.performanceMetrics?.previous - analyticsData?.performanceMetrics?.responseTime?.current) / analyticsData?.performanceMetrics?.responseTime?.previous * 100)?.toFixed(0)}%
                  </span>
                </div>
                <p className="text-sm text-gray-600 mb-1">Avg Response Time</p>
                <p className="text-2xl font-bold text-gray-900">{analyticsData?.performanceMetrics?.responseTime?.current}h</p>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Target className="w-5 h-5 text-green-600" />
                  </div>
                  <span className="text-xs text-green-600 font-medium">
                    +{((analyticsData?.performanceMetrics?.winRate?.current - analyticsData?.performanceMetrics?.winRate?.previous) / analyticsData?.performanceMetrics?.winRate?.previous * 100)?.toFixed(0)}%
                  </span>
                </div>
                <p className="text-sm text-gray-600 mb-1">Win Rate</p>
                <p className="text-2xl font-bold text-gray-900">{analyticsData?.performanceMetrics?.winRate?.current}%</p>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <DollarSign className="w-5 h-5 text-yellow-600" />
                  </div>
                  <span className="text-xs text-green-600 font-medium">
                    +{((analyticsData?.performanceMetrics?.avgProjectValue?.current - analyticsData?.performanceMetrics?.avgProjectValue?.previous) / analyticsData?.performanceMetrics?.avgProjectValue?.previous * 100)?.toFixed(0)}%
                  </span>
                </div>
                <p className="text-sm text-gray-600 mb-1">Avg Project Value</p>
                <p className="text-2xl font-bold text-gray-900">${analyticsData?.performanceMetrics?.avgProjectValue?.current?.toLocaleString()}</p>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Users className="w-5 h-5 text-purple-600" />
                  </div>
                  <span className="text-xs text-green-600 font-medium">
                    +{((analyticsData?.performanceMetrics?.clientSatisfaction?.current - analyticsData?.performanceMetrics?.clientSatisfaction?.previous) / analyticsData?.performanceMetrics?.clientSatisfaction?.previous * 100)?.toFixed(0)}%
                  </span>
                </div>
                <p className="text-sm text-gray-600 mb-1">Client Satisfaction</p>
                <p className="text-2xl font-bold text-gray-900">{analyticsData?.performanceMetrics?.clientSatisfaction?.current}/5</p>
              </div>
            </div>

            {/* Quote Trends Chart */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Quote Submission Trends</h3>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                    <span className="text-sm text-gray-600">Quotes Submitted</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    <span className="text-sm text-gray-600">Conversions</span>
                  </div>
                </div>
              </div>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={analyticsData?.quoteSubmissions}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="quotes" fill="#3b82f6" name="Quotes" />
                    <Bar dataKey="conversions" fill="#10b981" name="Conversions" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Project Categories */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">Project Categories Distribution</h3>
              <div className="flex flex-col lg:flex-row items-center gap-8">
                <div className="w-full lg:w-1/2 h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={analyticsData?.projectCategories}
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                        label={({ name, percent }) => `${name} ${(percent * 100)?.toFixed(0)}%`}
                      >
                        {analyticsData?.projectCategories?.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry?.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="w-full lg:w-1/2">
                  <div className="space-y-4">
                    {analyticsData?.projectCategories?.map((category, index) => (
                      <div key={index} className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-4 h-4 rounded-full"
                            style={{ backgroundColor: category?.color }}
                          ></div>
                          <span className="text-sm font-medium text-gray-700">{category?.name}</span>
                        </div>
                        <span className="text-sm text-gray-600">{category?.value}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'advertising' && (
          <div className="space-y-8">
            {/* Campaign Actions */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">Advertising Campaigns</h3>
                  <p className="text-sm text-gray-600">Manage your sponsored listings and promotional campaigns</p>
                </div>
                <button className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors">
                  <Plus className="w-4 h-4" />
                  Create Campaign
                </button>
              </div>
            </div>

            {/* Campaign Overview Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <DollarSign className="w-5 h-5 text-green-600" />
                  </div>
                </div>
                <p className="text-sm text-gray-600 mb-1">Total Budget</p>
                <p className="text-2xl font-bold text-gray-900">${campaigns?.reduce((sum, c) => sum + c?.budget, 0)}</p>
                <p className="text-sm text-gray-500 mt-2">
                  Spent: ${campaigns?.reduce((sum, c) => sum + c?.spent, 0)} ({((campaigns?.reduce((sum, c) => sum + c?.spent, 0) / campaigns?.reduce((sum, c) => sum + c?.budget, 0)) * 100)?.toFixed(0)}%)
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Eye className="w-5 h-5 text-blue-600" />
                  </div>
                </div>
                <p className="text-sm text-gray-600 mb-1">Total Impressions</p>
                <p className="text-2xl font-bold text-gray-900">{campaigns?.reduce((sum, c) => sum + c?.impressions, 0)?.toLocaleString()}</p>
                <p className="text-sm text-gray-500 mt-2">
                  CTR: {((campaigns?.reduce((sum, c) => sum + c?.clicks, 0) / campaigns?.reduce((sum, c) => sum + c?.impressions, 0)) * 100)?.toFixed(2)}%
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Target className="w-5 h-5 text-purple-600" />
                  </div>
                </div>
                <p className="text-sm text-gray-600 mb-1">Total Leads</p>
                <p className="text-2xl font-bold text-gray-900">{campaigns?.reduce((sum, c) => sum + c?.leads, 0)}</p>
                <p className="text-sm text-gray-500 mt-2">
                  CPL: ${(campaigns?.reduce((sum, c) => sum + c?.spent, 0) / campaigns?.reduce((sum, c) => sum + c?.leads, 0))?.toFixed(2)}
                </p>
              </div>
            </div>

            {/* Campaign List */}
            <div className="space-y-4">
              {campaigns?.map((campaign) => (
                <div key={campaign?.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 mb-2">{campaign?.name}</h3>
                          <div className="flex items-center gap-2 mb-3">
                            <span className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(campaign?.status)}`}>
                              {getStatusIcon(campaign?.status)}
                              {campaign?.status?.toUpperCase()}
                            </span>
                            <span className="text-sm text-gray-500">
                              {new Date(campaign.startDate)?.toLocaleDateString()} - {new Date(campaign.endDate)?.toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
                            <Edit className="w-4 h-4" />
                          </button>
                          <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
                            <Settings className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                        <div>
                          <p className="text-sm text-gray-600">Budget</p>
                          <p className="font-semibold text-gray-900">${campaign?.budget}</p>
                          <p className="text-xs text-gray-500">Spent: ${campaign?.spent}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Impressions</p>
                          <p className="font-semibold text-gray-900">{campaign?.impressions?.toLocaleString()}</p>
                          <p className="text-xs text-gray-500">CTR: {campaign?.ctr}%</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Clicks</p>
                          <p className="font-semibold text-gray-900">{campaign?.clicks}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Leads</p>
                          <p className="font-semibold text-gray-900">{campaign?.leads}</p>
                          <p className="text-xs text-gray-500">CPL: ${campaign?.cpl}</p>
                        </div>
                      </div>

                      <div className="mb-4">
                        <p className="text-sm text-gray-600 mb-2">Keywords:</p>
                        <div className="flex flex-wrap gap-2">
                          {campaign?.keywords?.map((keyword, index) => (
                            <span key={index} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">
                              {keyword}
                            </span>
                          ))}
                        </div>
                      </div>

                      <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                        <div 
                          className="bg-green-600 h-2 rounded-full" 
                          style={{ width: `${(campaign?.spent / campaign?.budget) * 100}%` }}
                        ></div>
                      </div>
                      <p className="text-xs text-gray-500">
                        {((campaign?.spent / campaign?.budget) * 100)?.toFixed(0)}% of budget used
                      </p>
                    </div>

                    <div className="flex lg:flex-col gap-2">
                      {campaign?.status === 'active' ? (
                        <button className="flex items-center gap-2 px-4 py-2 text-yellow-600 border border-yellow-200 rounded-lg hover:bg-yellow-50 transition-colors">
                          <Pause className="w-4 h-4" />
                          Pause
                        </button>
                      ) : (
                        <button className="flex items-center gap-2 px-4 py-2 text-green-600 border border-green-200 rounded-lg hover:bg-green-50 transition-colors">
                          <Play className="w-4 h-4" />
                          Resume
                        </button>
                      )}
                      <button className="flex items-center gap-2 px-4 py-2 text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors">
                        <BarChart3 className="w-4 h-4" />
                        Analytics
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Campaign Builder CTA */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
              <TrendingUp className="w-12 h-12 text-green-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Boost Your Visibility</h3>
              <p className="text-gray-600 mb-6">Create targeted advertising campaigns to reach more potential clients in your area.</p>
              <button className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors">
                Launch New Campaign
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VendorAnalyticsAdvertising;